import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../assets/css/home.css';
import appLogo from '../../assets/img/smiles.svg';
import googleIcon from '../../assets/img/google.svg';
import appToast, { TOAST_TYPE } from '../../utils/AppToast';
import { selectLoader } from '../../utils/Helpers';
import { useAuth } from '../../contexts/AuthContext';
import { disconnectGoogleCalendar, exportEventsToGoogleCalendar } from '../../utils/googleCalendar';
import { connectSlackActivity, disconnectSlackActivity } from '../../utils/slackActivity';
import leftArrow from '../../assets/img/white-left-icon.svg';
import Header from 'components/Header';

import googleCalendar from '../../assets/img/google-calendar.svg';
import slackSvgrepo from '../../assets/img/slack-svgrepo.svg';
import connectIcon from '../../assets/img/connect-icon.svg';
import checkIcon2 from '../../assets/img/check-icon2.svg';
import googleLogo2 from '../../assets/img/GoogleLogo2.svg';
import manageConnect from '../../assets/img/manage-connect.svg';
import disconnect from '../../assets/img/disconnect.svg';
import disconnectModal from '../../assets/img/disconnect_modal.svg';
import syncIcon from '../../assets/img/syncIcon.svg';

// Slack integration is temporarily hidden from the UI.
const SHOW_SLACK_INTEGRATION = false;

const HAPPY_ENDING_MCP_URL = 'https://api.happyending.ai/mcp';

const CLAUDE_CONNECTOR_STEPS = [
  {
    title: 'Go to Connectors',
    description: (
      <>In Claude, navigate to <strong>Customize &gt; Connectors</strong>.</>
    )
  },
  {
    title: 'Add custom connector',
    description: <>Click <strong>+</strong>, then select <strong>Add custom connector</strong>.</>
  },
  {
    title: 'Paste your MCP URL',
    description: <>Enter the Happy Ending MCP server URL shown below.</>
  },
  {
    title: 'Add OAuth (optional)',
    description: (
      <>Under <strong>Advanced settings</strong>, add your OAuth Client ID and Client Secret if required.</>
    )
  },
  {
    title: 'Authenticate',
    description: (
      <>Click <strong>Add</strong>, then sign in through the OAuth flow to connect your account.</>
    )
  },
  {
    title: 'Enable per chat',
    description: (
      <>In any conversation, enable the connector from <strong>+ &gt; Connectors</strong>.</>
    )
  }
];

const formatIntegrationSyncTime = (value) => {
  if (!value) return 'Not synced yet';

  const syncDate = new Date(value);
  const diffMs = Date.now() - syncDate.getTime();

  if (!Number.isFinite(diffMs) || diffMs < 0) return 'Just now';

  const diffSeconds = Math.floor(diffMs / 1000);
  if (diffSeconds < 60) return 'Just now';

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes} min ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return syncDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: syncDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
};

const getIntegrationSyncStatus = (status) => {
  if (status === 'failed') {
    return {
      label: 'Sync failed',
      className: 'sync-failed'
    };
  }

  if (status === 'success') {
    return {
      label: 'Up to date',
      className: 'sync-positive'
    };
  }

  return {
    label: 'Not synced yet',
    className: 'sync-muted'
  };
};

const IntegrationStatusBadge = ({ connected }) => (
  <span className={`integration-status ${connected ? 'connected' : ''}`}>
    <span />
    {connected ? 'Connected' : 'Not Connected'}
  </span>
);

const IntegrationCard = ({
  title,
  subtitle,
  description,
  connected,
  loading,
  icon,
  account,
  lastSync,
  syncStatus,
  syncError,
  manageUrl,
  onConnectClick,
  onDisconnectClick
}) => {
  const statusDetails = getIntegrationSyncStatus(syncStatus);

  return (
  <div className={`integration-card ${connected ? 'is-connected' : ''}`}>
    <div className="integration-card-main">
      <div className="integration-icon">{icon}</div>
      <div className="integration-copy">
        <div className="integration-title-row">
          <h3>{title}</h3>
          <IntegrationStatusBadge connected={connected} />
        </div>
        <p className="integration-subtitle">{subtitle}</p>
      </div>
      <button
        type="button"
        className={`integration-action-btn ${connected ? 'disconnect' : ''}`}
        disabled={loading}
        onClick={connected ? onDisconnectClick : onConnectClick}>
        {loading ? (
          selectLoader(14, '0px')
        ) : (
          <>
            <img src={connected ? disconnect : connectIcon} alt={connected ? 'Disconnect' : 'Connect'} />
            {connected ? 'Disconnect' : 'Connect'}
          </>
        )}
      </button>
    </div>

    <p className="integration-description">{description}</p>

    {connected && (
      <div className="integration-details">
        <div>
          <span>Account</span>
          <strong>{account || '-'}</strong>
        </div>
        <div>
          <span>Last Sync</span>
          <strong className={statusDetails.className}><img src={syncIcon} alt="Sync" /> {lastSync || 'Not synced yet'}</strong>
        </div>
        <div>
          <span>Sync Status</span>
          <strong className={statusDetails.className} title={syncError || ''}>● {statusDetails.label}</strong>
        </div>
        {syncError && <p className="integration-sync-error">{syncError}</p>}
      </div>
    )}
    {connected && manageUrl && (
      <div
        className="manage-connection cursor_pointer"
        role="button"
        tabIndex={0}
        onClick={() => window.open(manageUrl, '_blank', 'noopener,noreferrer')}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            window.open(manageUrl, '_blank', 'noopener,noreferrer');
          }
        }}>
        Manage Connection <img src={manageConnect} alt="Manage Connection" />
      </div>
    )}
  </div>
  );
};

const IntegrationConfirmModal = ({ integrationName, onClose, onConfirm, loading }) => (
  <div className="integration-modal-backdrop">
    <div className="integration-dialog integration-disconnect-dialog">
      <button type="button" className="integration-dialog-close" onClick={onClose} disabled={loading}>
        ×
      </button>
      <div className="integration-dialog-title">
        <img src={disconnectModal} alt="Slack" />
        <h3>Disconnect Integration?</h3>
      </div>
      <p>
        Are you sure you want to disconnect {integrationName}? Synchronization and notifications will stop immediately.
        You can reconnect at any time.
      </p>
      <button type="button" className="integration-dialog-primary" onClick={onConfirm} disabled={loading}>
        {loading ? selectLoader(16, '0px') : 'Disconnect'}
      </button>
    </div>
  </div>
);

const IntegrationIntroModal = ({ type, onClose, onContinue, loading }) => {
  const isGoogle = type === 'google';
  const title = isGoogle ? 'Connect Google Calendar' : 'Connect Slack';
  const icon = isGoogle ? <img src={googleLogo2} alt="" /> : <img src={slackSvgrepo} alt="" />;
  const points = isGoogle
    ? ['Sync tasks and subtasks as calendar events', 'Automatic two-way updates', 'Choose which boards to sync']
    : [
        'Receive Activity tab updates in Slack',
        'Post project updates, comments, and important activity',
        'Disconnect whenever you want'
      ];

  return (
    <div className="integration-modal-backdrop">
      <div className="integration-dialog">
        <button type="button" className="integration-dialog-close" onClick={onClose} disabled={loading}>
          ×
        </button>
        <div className="integration-dialog-title">
          <span className="integration-dialog-icon">{icon}</span>
          <h3>{title}</h3>
        </div>
        <p>
          {isGoogle
            ? 'Sign in with your Google account to enable calendar synchronization. Your Kanban tasks, subtasks, and checklists will automatically appear in Google Calendar.'
            : 'Sign in with Slack to receive project activity updates directly in your selected Slack workspace/channel.'}
        </p>
        <ul className="integration-benefits">
          {points.map((point) => (
            <li key={point}><img src={checkIcon2} alt="Check" /> {point}</li>
          ))}
        </ul>
        <div className="integration-dialog-actions">
          <button
            type="button"
            className={`integration-provider-btn ${isGoogle ? '' : 'integration-provider-btn-slack'}`}
            onClick={onContinue}
            disabled={loading}>
            {loading ? (
              selectLoader(16, '0px')
            ) : (
              <>
                {icon}
                Continue with {isGoogle ? 'Google' : 'Slack'}
              </>
            )}
          </button>
          <button type="button" className="integration-dialog-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const IntegrationSuccessModal = ({ type, onClose }) => {
  const isGoogle = type === 'google';

  return (
    <div className="integration-modal-backdrop">
      <div className="integration-dialog integration-success-dialog">
        <button type="button" className="integration-dialog-close" onClick={onClose}>
          ×
        </button>
        <div className="integration-dialog-title">
          <span className="integration-dialog-icon">
            {isGoogle ? <img src={googleCalendar} alt="" /> : <img src={slackSvgrepo} alt="" />}
          </span>
          <h3>Connect {isGoogle ? 'Google Calendar' : 'Slack'}</h3>
        </div>
        <div className="integration-success-check">✓</div>
        <h4>Successfully connected!</h4>
        <p>{isGoogle ? 'Syncing your calendar now...' : 'Slack activity sync is now active.'}</p>
      </div>
    </div>
  );
};

const ClaudeConnectorGuide = () => {
  const [copied, setCopied] = useState(false);

  const copyMcpUrl = async () => {
    try {
      await navigator.clipboard.writeText(HAPPY_ENDING_MCP_URL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      appToast('Unable to copy the MCP URL. Please copy it manually.', TOAST_TYPE.ERROR);
    }
  };

  return (
    <section className="claude-connector-section" aria-labelledby="claude-connector-title">
      <div className="claude-connector-heading">
        <div className="claude-connector-mark" aria-hidden="true">
          <svg viewBox="0 0 32 32" role="presentation">
            <path d="M16 5v22M5 16h22M8.2 8.2l15.6 15.6M23.8 8.2 8.2 23.8M10.7 4.7l10.6 22.6M27.3 10.7 4.7 21.3" />
          </svg>
        </div>
        <div>
          <p className="claude-connector-eyebrow">MCP CONNECTOR</p>
          <h2 id="claude-connector-title">Connect Happy Ending to Claude</h2>
          <p>Follow these steps to access your Happy Ending workspace from Claude conversations.</p>
        </div>
      </div>

      <ol className="claude-connector-steps">
        {CLAUDE_CONNECTOR_STEPS.map((step, index) => (
          <li key={step.title}>
            <span className="claude-step-number">{index + 1}</span>
            <div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
              {index === 2 && (
                <div className="claude-mcp-url">
                  <code>{HAPPY_ENDING_MCP_URL}</code>
                  <button type="button" onClick={copyMcpUrl} aria-label="Copy MCP server URL">
                    {copied ? 'Copied!' : 'Copy URL'}
                  </button>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
};

const Integrations = () => {
  const navigate = useNavigate();
  const { user, fetchUserInfo, socket } = useAuth();
  const [activeIntro, setActiveIntro] = useState(null);
  const [successType, setSuccessType] = useState(null);
  const [disconnectType, setDisconnectType] = useState(null);
  const [loadingType, setLoadingType] = useState(null);

  const userEmail = user?.email || '';
  const integrations = useMemo(
    () => ({
      google: {
        title: 'Google Calendar',
        subtitle: 'calendar.google.com',
        connected: Boolean(user?.googleCalendarConnected),
        account: userEmail,
        lastSync: formatIntegrationSyncTime(user?.googleCalendarIntegration?.lastSyncedAt),
        syncStatus: user?.googleCalendarIntegration?.lastSyncStatus || 'not_synced',
        syncError: user?.googleCalendarIntegration?.lastSyncError || '',
        description:
          'Sync your Kanban tasks, subtasks, and checklists with your Google Calendar. Changes made in the app are automatically reflected in your calendar.',
        icon: <img src={googleCalendar} alt="Google Calendar" />,
        manageUrl: 'https://calendar.google.com'
      },
      slack: {
        title: 'Slack',
        subtitle: 'slack.com',
        connected: Boolean(user?.slackActivityConnected),
        account: user?.slackActivityIntegration?.channelName
          ? `#${user.slackActivityIntegration.channelName}`
          : user?.slackActivityIntegration?.teamName || userEmail,
        lastSync: formatIntegrationSyncTime(user?.slackActivityIntegration?.lastSyncedAt),
        syncStatus: user?.slackActivityIntegration?.lastSyncStatus || 'not_synced',
        syncError: user?.slackActivityIntegration?.lastSyncError || '',
        description:
          'Receive project activity, task updates, comments, assignments, and other important notifications directly in Slack.',
        icon: <img src={slackSvgrepo} alt="Slack" />,
        manageUrl: 'https://app.slack.com'
      }
    }),
    [
      user?.googleCalendarConnected,
      user?.googleCalendarIntegration?.lastSyncError,
      user?.googleCalendarIntegration?.lastSyncStatus,
      user?.googleCalendarIntegration?.lastSyncedAt,
      user?.slackActivityConnected,
      user?.slackActivityIntegration?.channelName,
      user?.slackActivityIntegration?.lastSyncError,
      user?.slackActivityIntegration?.lastSyncStatus,
      user?.slackActivityIntegration?.lastSyncedAt,
      user?.slackActivityIntegration?.teamName,
      userEmail,
    ]
  );

  useEffect(() => {
    if (!user?._id) return;

    fetchUserInfo();
    // Fetch once when navigating to Integrations with an already-loaded user.
    // On hard reload, AuthContext already performs the initial user fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!socket) return undefined;

    const handleIntegrationSyncUpdated = () => {
      fetchUserInfo();
    };

    socket.on('IntegrationSyncUpdated', handleIntegrationSyncUpdated);

    return () => {
      socket.off('IntegrationSyncUpdated', handleIntegrationSyncUpdated);
    };
  }, [fetchUserInfo, socket]);

  const handleConnect = async (type) => {
    if (loadingType) return;

    setLoadingType(type);
    try {
      if (type === 'google') {
        await exportEventsToGoogleCalendar({
          connected: Boolean(user?.googleCalendarConnected)
        });
      } else {
        await connectSlackActivity();
      }

      await fetchUserInfo();
      setActiveIntro(null);
      setSuccessType(type);
      appToast(`${integrations[type].title} connected successfully.`, TOAST_TYPE.SUCCESS);
    } catch (error) {
      console.error(`${type} integration connect failed:`, error);
      appToast(error.message || `Unable to connect ${integrations[type].title}.`, TOAST_TYPE.ERROR);
    } finally {
      setLoadingType(null);
    }
  };

  const handleDisconnect = async () => {
    if (!disconnectType || loadingType) return;

    setLoadingType(disconnectType);
    try {
      if (disconnectType === 'google') {
        await disconnectGoogleCalendar();
      } else {
        await disconnectSlackActivity();
      }

      await fetchUserInfo();
      appToast(`${integrations[disconnectType].title} disconnected successfully.`, TOAST_TYPE.SUCCESS);
      setDisconnectType(null);
    } catch (error) {
      console.error(`${disconnectType} integration disconnect failed:`, error);
      appToast(error.message || `Unable to disconnect ${integrations[disconnectType].title}.`, TOAST_TYPE.ERROR);
    } finally {
      setLoadingType(null);
    }
  };

  const HeaderContent = (
    <>
      <h2>Integrations</h2>
      <div className="head-right">
        <a onClick={() => navigate(-1)}>
          <img src={leftArrow} alt="comp1" className="left-arrow" />
        </a>
      </div>
    </>
  );

  return (
    <div className="integrations-page">
      <Header content={HeaderContent} />

      <main className="integrations-body">
        <p className="integrations-intro">
          Connect your favorite apps to keep your projects in sync and receive updates where you work.
        </p>

        <section className="integrations-panel">
          <IntegrationCard
            {...integrations.google}
            loading={loadingType === 'google'}
            onConnectClick={() => setActiveIntro('google')}
            onDisconnectClick={() => setDisconnectType('google')}
          />
          {SHOW_SLACK_INTEGRATION && (
            <IntegrationCard
              {...integrations.slack}
              loading={loadingType === 'slack'}
              onConnectClick={() => setActiveIntro('slack')}
              onDisconnectClick={() => setDisconnectType('slack')}
            />
          )}
        </section>

        <ClaudeConnectorGuide />
      </main>

      {activeIntro && (
        <IntegrationIntroModal
          type={activeIntro}
          loading={loadingType === activeIntro}
          onClose={() => setActiveIntro(null)}
          onContinue={() => handleConnect(activeIntro)}
        />
      )}

      {successType && <IntegrationSuccessModal type={successType} onClose={() => setSuccessType(null)} />}

      {disconnectType && (
        <IntegrationConfirmModal
          integrationName={integrations[disconnectType].title}
          loading={loadingType === disconnectType}
          onClose={() => setDisconnectType(null)}
          onConfirm={handleDisconnect}
        />
      )}
    </div>
  );
};

export default Integrations;
